import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {concat, Observable, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap, tap} from 'rxjs/operators';
import {Address} from '../../interfaces/address';
import {Field} from '../../interfaces/field';

import {FieldErrorService} from '../../services/field-error.service';
import {AIRPORTS} from '../../data/airports';
import {ApiService} from "../../services/api.service";
import {StateService} from "../../services/state.service";
import {StorageMap} from "@ngx-pwa/local-storage";
import {TranslateService} from "@ngx-translate/core";

declare let google: any;

const PRO6PP_AUTH_KEY = 'B1uhxwCW6FKNPQI1';

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

@Component({
    selector: 'app-address-field',
    templateUrl: './address-field.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./address-field.component.scss'],
    providers: [ApiService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddressFieldComponent implements OnInit, AfterViewInit {
  /** Character count the autocomplete starts at when a field sets no `autocompleteStartAt`. */
  static readonly DEFAULT_AUTOCOMPLETE_START_AT = 3;

  /** Upper clamp: past this the field would look broken rather than throttled. */
  static readonly MAX_AUTOCOMPLETE_START_AT = 10;

  private useLegacyPlaces: boolean = sessionStorage.getItem('useLegacyPlaces') === '1';

  @Input() config: Field;
  @Input() form: UntypedFormGroup;
  @Input() type: string;
  @Input() showAddStopOver: boolean;
  @Input() sortableErrors: FieldErrorService;
  @Output() openHelpModal: EventEmitter<any> = new EventEmitter<any>();
  @Output() checkWorkArea: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('inputAutoComplete') inputAutoComplete: any;

  results: Observable<any>;
  loading = false;
  currentTerm: string;
  input = new Subject<string>();
  geocodeService: any;
  autocompleteService: any;
  autocompleteSessionToken: string;
  autocompleteSettings = {
    includedRegionCodes: []
  };
  currentLocation: {
    lat: number,
    lng: number
  } = null;
  hasLocationActive = false;
  address: Address;
  data: any[] = [];
  area: any;
  hasErrors = false;
  airports = [];
  pois = [];
  foundAirports = [];
  foundPois = [];
  private _historyListenerAdded = false;
  private _explicitlyCleaned = false;
  private _inputEl: HTMLInputElement | null = null;
  private _suppressRecentOnFocus = false;

  constructor(@Inject(DOCUMENT) private document: Document,
              private apiService: ApiService,
              private _vault: StorageMap,
              protected _state: StateService,
              private _translate: TranslateService,
              public errors: FieldErrorService) {

  }

  openPanel(): void {
    const self = this;
    setTimeout(function () {
      self.inputAutoComplete.open();
    }, 100);
  }

  focusInput(): void {
    setTimeout(() => {
      const input = document.querySelector(
        `#autoComplete-${this.config.property} input`
      ) as HTMLInputElement | null;
      input?.focus();
    }, 100);
  }

  getIcon(types: string[] = [], type: string = ''): string {
    if (types.includes('airport') || type === 'airport') {
      return 'plane';
    } else if (types.includes('station') || types.includes('nsstation') || types.includes('transit_station') || types.includes('train_station') || type === 'station' || type === 'nsstation') {
      return 'train';
    } else if (types.includes('poi')) {
      return 'search-location';
    } else {
      return 'map-marker';
    }
  }

  geoCodeAddress(prediction: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.geocodeService) {
        this.geocodeService = new google.maps.Geocoder();
      }

      this.geocodeService.geocode({placeId: prediction.place_id}, (results, status) => {
        if (status === 'OK') {
          const data = results[0];
          // console.log(`getAddress() - prediction`, prediction);
          // console.log(`getAddress() - data`, data);
          const address: any = {};
          address.gps = {};
          address.gps.lat = data.geometry.location.lat();
          address.gps.lng = data.geometry.location.lng();
          address.internationalAlias = prediction.description;

          if (data.address_components) {
            data.address_components.forEach((component) => {

              if (component.types[0] === 'postal_code' || component.types[0] === 'postal_code_prefix') {
                address.postalCode = component.short_name.replace(' ', '');
              }
              if (component.types[0] === 'locality') {
                address.city = component.short_name;
              }
              if (component.types[0] === 'country') {
                address.countryCode = component.short_name;
              }
              if (component.types[0] === 'route') {
                address.streetName = component.short_name;
              }
              if (component.types[0] === 'street_number') {
                address.houseNumber = component.short_name.replace(' ', '');
              }
            });
          }
          return resolve(address);
        } else {
          return reject('Geocoder failed due to: ' + status);
        }
      });
    });
  }

  getAddress(prediction: any) {
    this.hasLocationActive = true;

    if (prediction?.recentAddress) {
      this.address = {
        type: prediction.type,
        code: prediction.code,
        gps: prediction.gps,
        internationalAlias: prediction.internationalAlias,
        city: prediction.city,
        postalCode: prediction.postalCode,
        streetName: prediction.streetName,
        houseNumber: prediction.houseNumber,
        countryCode: prediction.countryCode,
        synonym: prediction.synonym,
      };
      this.storeAddress();
      return;
    }

    if (prediction?.inferSuggestion) {
      // Partial suggestion (street/city, no concrete house number): feed the label back into
      // the resolver so the user narrows down (street -> number -> addition).
      if (prediction.inferValue?.street_number == null) {
        this.search(prediction.description);
        this.openPanel();
        // Selecting a partial (street only) refines the list — keep the caret in the input so
        // the user can keep typing the house number without re-clicking the field.
        this.focusInput();
        return null;
      }
      // Complete suggestion: resolve coordinates before storing.
      this.resolveInferAddress(prediction.inferValue).then((address) => {
        if (!address) {
          this.hasErrors = true;
          this.hasLocationActive = false;
          this.address = null;
          const patch = {};
          patch[`${this.config.property}Model`] = null;
          this.form.patchValue(patch);
          this.checkFieldValidity();
          return;
        }
        this.address = address;
        this.storeAddress();
      });
      return null;
    }

    if (typeof prediction === 'string' || !prediction) {
      this.hasErrors = true;
      this.hasLocationActive = false;
      this.address = null;
      const patch = {};
      patch[`${this.config.property}Model`] = null;
      this.form.patchValue(patch);
      this.checkFieldValidity();
      return null;
    }

    const address: any = {
      type: prediction.type || 'generic',
      code: prediction.code,
      gps: {}
    };

    if (prediction.types && prediction.types.includes('address')) {
      address.synonym = prediction.formatted_address;
    }
    if (!prediction.custom) {
      this.geoCodeAddress(prediction).then(result => {
        Object.keys(result).forEach((key) => {
          address[key] = result[key];
        });

        if (prediction.types && prediction.types.includes('airport') && address.internationalAlias) {
          address.type = 'airport';
          address.synonym = this.formatAlias(address.internationalAlias);
        } else if (prediction.types && (prediction.types.includes('station') || prediction.types.includes('nsstation'))) {
          address.type = 'poi';
          prediction.formatted_address = prediction.name;
          address.synonym = prediction.name;
          address.gps.lat = prediction.lat;
          address.gps.lng = prediction.lng;
          address.postalCode = prediction.postCode;
          address.streetName = prediction.streetName;
          address.city = prediction.city;
          address.countryCode = prediction.countryCode;
        } else if (prediction.types && prediction.types.includes('point_of_interest')) {
          address.type = 'poi';
          address.synonym = this.formatAlias(address.internationalAlias);
        }

        this.address = address;
        this.storeAddress();
      });
    } else {
      if (!address.gps.lat) {
        address.gps.lat = (prediction.gps ? prediction.gps.lat : prediction.lat);
        address.gps.lng = (prediction.gps ? prediction.gps.lng : prediction.lng);
      }
      address.internationalAlias = prediction.formatted_address;
      if (prediction.housenumber) {
        address.houseNumber = prediction.housenumber;
      }
      this.address = address;
      this.storeAddress();
    }
  }

  formatAlias(alias: string): string {
    // Match "Name (CODE)" pattern
    const match = alias.match(/^([^"]+)(?:\s"[^"]+")?\s\((\w{3})\)/);
    if (match) {
      const airport = match[1].trim();
      const code = match[2].trim();
      return `${airport} (${code})`;
    }
    return alias; // fallback if it doesn't match
  }

  storeAddress() {
    const patch = {};
    if (this.config.formArray) {
      patch['stopOvers'] = [];
      patch['stopOversModel'] = [];
      patch['stopOvers'][this.config.property] = this.address.internationalAlias;
      patch['stopOversModel'][this.config.property] = this.address;
    } else {
      patch[`${this.config.property}Model`] = this.address;
      patch[this.config.property] = this.address.internationalAlias;
    }

    this.form.patchValue(patch);
    this.saveRecentAddress();
    this.checkWorkArea.emit(this.config.property);
    this.checkFieldValidity();
    // Selection complete — empty the dropdown source so simply focusing/clicking the now-filled
    // field no longer reopens the autocomplete. It only reopens once the user types again.
    this.data = [];
  }

  private recentAddressesKey(): string {
    return 'recentAddresses';
  }

  private saveRecentAddress(): void {
    if (!this.address?.internationalAlias || !this.address?.gps) return;
    const key = this.recentAddressesKey();
    let recent: any[] = [];
    try {
      recent = JSON.parse(localStorage.getItem(key) || '[]');
    } catch { recent = []; }

    const item = {
      custom: true,
      recentAddress: true,
      description: this.address.internationalAlias,
      formatted_address: this.address.internationalAlias,
      internationalAlias: this.address.internationalAlias,
      type: this.address.type || 'generic',
      types: ['recentAddress'],
      gps: this.address.gps,
      city: this.address.city,
      postalCode: this.address.postalCode,
      streetName: this.address.streetName,
      houseNumber: this.address.houseNumber,
      countryCode: this.address.countryCode,
      code: this.address.code,
      synonym: this.address.synonym,
    };
    const filtered = recent.filter(r => r.description !== item.description);
    try {
      localStorage.setItem(key, JSON.stringify([item, ...filtered].slice(0, 3)));
    } catch { /* storage full or blocked — skip silently */ }
  }

  getRecentAddresses(): any[] {
    let recent: any[];
    try {
      recent = JSON.parse(localStorage.getItem(this.recentAddressesKey()) || '[]');
    } catch {
      return [];
    }
    // Hide addresses already chosen in another address field of this form: the pickup is always
    // the newest "recent", so it would otherwise top the list when picking the destination, an
    // option that is practically never wanted.
    const taken = new Set<string>();
    const controls = (this.form as any)?.controls || {};
    Object.keys(controls).forEach(key => {
      if (key === `${this.config.property}Model` || !key.endsWith('Model')) return;
      const alias = controls[key]?.value?.internationalAlias;
      if (alias) taken.add(alias);
    });
    return taken.size ? recent.filter(r => !taken.has(r.internationalAlias || r.description)) : recent;
  }

  checkFieldValidity(): void {
    if (!this.address) {
      return;
    }
    // console.log(`[AddressFieldComponent].checkFieldValidity for ${this.config.property}`);
    if (this.config.houseNumberRequired && !this.address.houseNumber && this.address.type === 'generic') {
      this.errors.addError({
        id: `${this.config.property}HouseNumber`,
        property: this.config.property,
        text: 'address_field_error_house_number',
        label: this.config.label,
      });
    } else {
      this.errors.removeError(`${this.config.property}HouseNumber`);
    }
    if (this.config.formArray) {
      // @ts-ignore
      if (this.form.controls['stopOvers'].controls[this.config.property].value) {
        this.errors.removeError(`${this.config.property}Required`);
      }
    } else {
      if (this.form.controls[this.config.property].value) {
        this.errors.removeError(`${this.config.property}Required`);
      }
    }
  }

  searchGoogle(term: string): Promise<any[]> {
    if (typeof google === 'undefined' || !google.maps?.places) {
      return Promise.resolve([]);
    }
    if (this.useLegacyPlaces) {
      return this.searchGoogleLegacy(term);
    }
    return this.searchGoogleNew(term);
  }

  searchGoogleNew(term: string): Promise<any[]> {
    return new Promise(async (resolve) => {
      if (!term || !term.trim()) {
        return resolve([]);
      }

      const countries = Object.keys(AIRPORTS) as Array<keyof typeof AIRPORTS>;
      const allAirports: any[] = countries.reduce<any[]>((acc, country) => {
        return acc.concat(AIRPORTS[country]);
      }, []);
      let matchedAirport: any | null = null;

      try {
        const request: any = {
          ...(this.autocompleteSettings || {}),
          input: term,
          language: this._state?.companySettings?.autocompleteLanguage,
          sessionToken: this.autocompleteSessionToken, // v3 token is ok
        };

        let resp: any;
        try {
          resp = await (google.maps.places as any)
            .AutocompleteSuggestion
            .fetchAutocompleteSuggestions(request)
            .catch((err: any) => { throw err; }); // forceer reject i.p.v. alleen console error
        }  catch (err) {
          console.warn("New Places API failed, falling back to legacy:", err);
          this.useLegacyPlaces = true;
          sessionStorage.setItem("useLegacyPlaces", "1");
          return this.searchGoogleLegacy(term).then(resolve);
        }

        const suggestions = resp?.suggestions;
        if (!Array.isArray(suggestions)) {
          // Library heeft intern gefaald (403, disabled API, domein niet geautoriseerd, etc.).
          // Val terug op de legacy service: die rapporteert REQUEST_DENIED expliciet, zodat een
          // domein-autorisatieprobleem wordt gedetecteerd i.p.v. stilzwijgend genegeerd.
          this.useLegacyPlaces = true;
          sessionStorage.setItem("useLegacyPlaces", "1");
          return this.searchGoogleLegacy(term).then(resolve);
        }

        // Suggestions came back, so the domain is authorized — clear any earlier flag.
        this.clearGoogleMisconfigError();

        const results: any[] = [];
        for (const s of suggestions) {
          const p = (s as any).placePrediction || s;

          const description =
            (p?.text?.toString?.() as string) ??
            [p?.structuredFormat?.mainText?.text, p?.structuredFormat?.secondaryText?.text]
              .filter(Boolean)
              .join(', ');

          if (!matchedAirport && description) {
            const found = allAirports.find(a => description.includes(a.code));
            if (found) {
              matchedAirport = found;
              continue;
            }
          }

          const types = Array.isArray(p?.types) ? [...p.types] : [];
          types.push('google');

          results.push({
            description,
            place_id: p?.placeId,
            placeId: p?.placeId,
            structured_formatting: {
              main_text: p?.structuredFormat?.mainText?.text ?? '',
              secondary_text: p?.structuredFormat?.secondaryText?.text ?? '',
            },
            types,
          });
        }

        if (matchedAirport) {
          results.unshift({
            custom: true,
            description: `${matchedAirport.name} (${matchedAirport.code})`,
            formatted_address: `${matchedAirport.name} (${matchedAirport.code})`,
            type: 'airport',
            types: ['airport', 'airportPoi'],
            gps: matchedAirport.gps,
            streetName: matchedAirport.streetName,
            houseNumber: matchedAirport.houseNumber,
            postalCode: matchedAirport.postalCode,
            city: matchedAirport.city,
            countryCode: matchedAirport.countryCode,
          });
        }

        resolve(results);
      } catch (e) {
        // extra safety net—zou zelden moeten triggeren
        const fallback = matchedAirport ? [{
          custom: true,
          description: `${matchedAirport.name} (${matchedAirport.code})`,
          formatted_address: `${matchedAirport.name} (${matchedAirport.code})`,
          type: 'airport',
          types: ['airport', 'airportPoi'],
          gps: matchedAirport.gps,
          streetName: matchedAirport.streetName,
          houseNumber: matchedAirport.houseNumber,
          postalCode: matchedAirport.postalCode,
          city: matchedAirport.city,
          countryCode: matchedAirport.countryCode,
        }] : [];
        resolve(fallback);
      }
    });
  }

  searchGoogleLegacy(term: string): Promise<any[]> {
    return new Promise((resolve) => {
      if (!term || !term.trim()) return resolve([]);
      if (typeof google === 'undefined' || !google.maps?.places) return resolve([]);

      const countries = Object.keys(AIRPORTS) as Array<keyof typeof AIRPORTS>;
      const allAirports: any[] = countries.reduce<any[]>((acc, country) => {
        return acc.concat(AIRPORTS[country]);
      }, []);
      let matchedAirport: any | null = null;

      // Lazily create legacy service if needed
      if (!this.autocompleteService) {
        this.autocompleteService = new google.maps.places.AutocompleteService();
      }
      // Ensure legacy componentRestrictions format if caller provided includedRegionCodes
      const settings = { ...(this.autocompleteSettings || {}) } as any;
      if (!settings.componentRestrictions && settings.includedRegionCodes) {
        settings.componentRestrictions = {
          country: settings.includedRegionCodes.map((c: string) => (c === 'EN' ? 'GB' : c)),
        };
        delete settings.includedRegionCodes;
      }

      this.autocompleteService.getPlacePredictions(
        {
          ...settings,
          input: term,
          language: this._state?.companySettings?.autocompleteLanguage,
          sessionToken: this.autocompleteSessionToken,
        },
        (predictions, status) => {
          const results: any[] = [];
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            // Legacy autocomplete succeeded — the domain is authorized, clear any earlier flag.
            this.clearGoogleMisconfigError();
            predictions.forEach(prediction => {
              if (!matchedAirport) {
                const found = allAirports.find(a => prediction.description.includes(a.code));
                if (found) { matchedAirport = found; return; }
              }
              prediction.types = Array.isArray(prediction.types) ? prediction.types : [];
              prediction.types.push('google');
              results.push(prediction);
            });
          } else if (status === google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
            // Google rejected the request — typically because this live domain is not in the
            // Maps key's HTTP-referrer allowlist. Flag it so the address field shows a clear
            // misconfiguration message instead of silently returning nothing.
            this._state.googleMapsAuthError = true;
            if (!this._state.googleMapsErrorUrl) {
              this._state.googleMapsErrorUrl = window.location.host;
            }
          }

          if (matchedAirport) {
            results.unshift({
              custom: true,
              description: `${matchedAirport.name} (${matchedAirport.code})`,
              formatted_address: `${matchedAirport.name} (${matchedAirport.code})`,
              type: 'airport',
              types: ['airport', 'airportPoi'],
              gps: matchedAirport.gps,
              streetName: matchedAirport.streetName,
              houseNumber: matchedAirport.houseNumber,
              postalCode: matchedAirport.postalCode,
              city: matchedAirport.city,
              countryCode: matchedAirport.countryCode,
            });
          }
          resolve(results);
        }
      );
    });
  }

  convertAirportToPoi(airport: any) {
    return {
      ...airport,
      custom: true,
      description: `${airport.name} (${airport.code})`,
      formatted_address: `${airport.name} (${airport.code})`,
      type: 'airport',
      types: [
        'airport',
        'airportPoi'
      ],
    };
  }

  convertPoi(poi: any) {
    return {
      ...poi,
      custom: true,
      description: `${poi.name}`,
      formatted_address: `${poi.name}`,
      type: poi.type,
      types: [
        poi.type
      ],
    };
  }

  customFilter(countries: any[], query: string): any[] {
    // return countries.filter(x => x.description.toLowerCase().startsWith(query.toLowerCase()));
    return this.data;
  };

  searchAirports(term: string): Promise<any[]> {
    return new Promise((resolve) => {
      const results = [];
      const escapedTerm = escapeRegExp(term);
      const regex = new RegExp(escapedTerm, 'gi');
      this.airports.forEach(airport => {
        const nameMatch = airport.name.match(regex);
        const codeMatch = airport.code.match(regex);
        if (nameMatch) {
          results.push(this.convertAirportToPoi(airport));
        } else if (codeMatch) {
          results.push(this.convertAirportToPoi(airport));
        }
      });
      this.foundAirports = results;
      return resolve(results);
    })
  }

  searchCustomPois(term: string): Promise<any[]> {
    return new Promise((resolve) => {
      const results = [];
      const escapedTerm = escapeRegExp(term);
      const regex = new RegExp(escapedTerm, 'gi');
      this.pois.forEach(poi => {
        const nameMatch = poi.name.match(regex);
        const codeMatch = poi.code.match(regex);
        if (nameMatch) {
          results.push(this.convertPoi(poi));
        } else if (codeMatch) {
          results.push(this.convertPoi(poi));
        }
      });
      this.foundPois = results;
      return resolve(results);
    })
  }

  /**
   * pro6pp "infer" backend (opt-in via config.autocompleteBackend === 'pro6ppInfer').
   * Progressive Dutch-address resolver: each call returns ranked suggestions with a stage.
   * Suggestions carry no coordinates — a complete one is resolved to gps in resolveInferAddress().
   * NL-only (uses /infer/nl).
   */
  searchPro6ppInfer(term: string): Promise<any[]> {
    return this.apiService.getCustomUrl(
      `https://api.pro6pp.nl/v2/infer/nl?query=${encodeURIComponent(term)}&authKey=${PRO6PP_AUTH_KEY}&limit=8`
    ).then((response) => {
      if (!response || response.error_id || !Array.isArray(response.suggestions)) {
        return [];
      }
      return response.suggestions.map((s) => ({
        custom: true,
        inferSuggestion: true,
        stage: response.stage,
        inferValue: s.value,
        description: s.label,
        formatted_address: s.label,
        internationalAlias: s.label,
        type: 'postcode',
      }));
    }).catch((error) => {
      console.log('pro6pp infer error', error);
      return [];
    });
  }

  /**
   * Resolves a complete infer suggestion (postcode + street_number) to a full Address with
   * coordinates, using the existing pro6pp autocomplete endpoint. Resolves null when no
   * coordinates can be obtained so the caller can surface an error instead of storing a
   * gps-less address.
   */
  resolveInferAddress(value: any): Promise<Address | null> {
    const houseNumber = value.addition ? `${value.street_number} ${value.addition}` : `${value.street_number}`;
    // When an address has multiple units the autocomplete endpoint requires the addition as
    // `premise`; without it the call fails with MISSING_PARAMETER and returns no coordinates.
    const premise = value.addition ? `&premise=${encodeURIComponent(value.addition)}` : '';
    return this.apiService.getCustomUrl(
      `https://api.pro6pp.nl/v2/autocomplete/nl?authKey=${PRO6PP_AUTH_KEY}&postalCode=${value.postcode}&streetNumber=${value.street_number}${premise}`
    ).then((r) => {
      if (!r || r.error_id || !r.lat || !r.lng) {
        return null;
      }
      const address: any = {
        type: 'generic',
        gps: { lat: r.lat, lng: r.lng },
        streetName: r.street ? r.street : value.street,
        houseNumber,
        postalCode: r.postalCode ? r.postalCode : value.postcode,
        city: r.settlement ? r.settlement : value.city,
        countryCode: r.countryCode ? r.countryCode : 'NL',
        internationalAlias: `${value.street} ${houseNumber}, ${value.postcode}, ${value.city}`,
      };
      return address;
    }).catch((error) => {
      console.log('pro6pp infer resolve error', error);
      return null;
    });
  }

  getLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.currentLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };

          this.hasLocationActive = true;
          this.loading = true;
          this.resolveCurrentAddress()
            .then((result) => {
              this.loading = false;
              this.getAddress(result);
            })
        },
        (error) => {
          this.hasLocationActive = false;
          this.getLocation();
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,       // 🔥 REQUIRED
          maximumAge: 0
        }
      );
    }
  }

  resolveCurrentAddress(): Promise<any> {
    const self = this;
    return new Promise((resolve, reject) => {
      if (!self.currentLocation) {
        return reject('No current location available');
      }

      if (!self.geocodeService) {
        self.geocodeService = new google.maps.Geocoder();
      }

      const latlng = {
        lat: self.currentLocation.lat,
        lng: self.currentLocation.lng,
      };
      self.geocodeService.geocode({ location: latlng }, (results, status) => {
        if (status !== 'OK' || !results || !results.length) {
          return reject('Reverse geocoding failed: ' + status);
        }

        const result = results[0];
        const address: any = {
          custom: true,
          description: result.formatted_address,
          formatted_address: result.formatted_address,
          internationalAlias: result.formatted_address,
          type: 'generic',
          gps: {
            lat: latlng.lat,
            lng: latlng.lng,
          },
        };

        if (result.address_components) {
          result.address_components.forEach((component) => {
            if (component.types.includes('street_number')) {
              address.housenumber = component.short_name;
            }
            if (component.types.includes('route')) {
              address.streetName = component.short_name;
            }
            if (component.types.includes('postal_code')) {
              address.postalCode = component.short_name.replace(' ', '');
            }
            if (component.types.includes('locality')) {
              address.city = component.short_name;
            }
            if (component.types.includes('country')) {
              address.countryCode = component.short_name;
            }
          });
        }
        resolve(address);
      });
    });
  }

  /**
   * Minimum number of typed characters before the autocomplete fires, from the field's
   * `autocompleteStartAt` setting. `minQueryLength` on ng-autocomplete gates the
   * `inputChanged` emit that drives search(), so this is what actually throttles the
   * Google/POI lookups — lowering it costs one billable Places call per extra keystroke.
   *
   * Defaults to 3, which is what the template hardcoded before the setting existed, so a
   * form config without the key keeps today's behaviour.
   *
   * Clamped at both ends. The floor of 1 matters for more than tidiness: at 0 an empty
   * query satisfies both of the library's branches and emits `inputChanged` twice per
   * keystroke, and search() treats an empty term as "show recent addresses" rather than a
   * lookup. The ceiling stops a mistyped or mis-pasted value (`30`) from silently killing
   * autocomplete altogether — no dropdown ever opens and nothing explains why, which is
   * indistinguishable from the Google misconfiguration `buildGoogleMisconfigError()`
   * exists to report.
   */
  get autocompleteStartAt(): number {
    // Form config arrives as JSON, so this may be a number, a numeric string, null, a
    // blank string, a boolean, or absent. Accept only the two shapes that can carry a
    // count: Number() would otherwise turn null/''/'  '/[] into 0 and true into 1, and
    // those are "not set", not "start at that many characters".
    const raw: any = this.config?.autocompleteStartAt;
    const usable = typeof raw === 'number' || (typeof raw === 'string' && raw.trim() !== '');

    if (!usable) {
      return AddressFieldComponent.DEFAULT_AUTOCOMPLETE_START_AT;
    }

    const configured = Number(raw);

    if (!Number.isFinite(configured)) {
      return AddressFieldComponent.DEFAULT_AUTOCOMPLETE_START_AT;
    }

    return Math.min(
      AddressFieldComponent.MAX_AUTOCOMPLETE_START_AT,
      Math.max(1, Math.floor(configured))
    );
  }

  search(term: string) {
    const self = this;
    this.loading = true;
    this.hasLocationActive = false;
    this.hasErrors = false;
    this.errors.removePropertyErrors(this.config.property);

    if (!term || !term.trim()) {
      this.data = this.getRecentAddresses();
      this.loading = false;
      return;
    }

    this.data = [];
    let airports = [];
    let pois = [];
    let googleResults = [];
    this.loading = false;
    return this.searchAirports(term)
      .then(results => {
        if (results) {
          airports = results;
          this.data = this.data.concat(airports);
        }
        return this.searchCustomPois(term);
      }).then(results => {
        if (results) {
          pois = results;
          this.data = this.data.concat(pois);
        }
        if (this.config.autocompleteBackend === 'pro6ppInfer') {
          return this.searchPro6ppInfer(term).then((inferResults) => {
            if (inferResults) {
              this.data = this.data.concat(inferResults);
            }
            this.loading = false;
          });
        }
        return this.searchGoogle(term).then((results) => {
          if (results) {
            this.data = this.data.concat(this.filterGoogleResults(results));
          }
          this.loading = false;

          // Google rejects autocomplete when the live domain is not authorized for the
          // customer's Maps key. The signal (gm_authFailure / REQUEST_DENIED / the
          // #google-error banner) can land just after the search resolves, so re-check
          // shortly after and, if there are still no results, replace the empty dropdown
          // with a clear misconfiguration message instead of leaving the field blank.
          setTimeout(() => {
            if (self.data && self.data.length > 0) return;
            const misconfig = self.buildGoogleMisconfigError();
            if (misconfig) {
              self.data = [misconfig];
              self.loading = false;
            }
          }, 150);
        });
      });
  }

  /**
   * Builds the inline "address lookup is misconfigured for this domain" row shown in place of
   * an empty autocomplete dropdown, or null when there is no misconfiguration.
   *
   * Detection sources (any one is enough):
   *  - this._state.googleMapsAuthError — set by gm_authFailure (works inside the inline
   *    Angular Element, where our index.html / #google-error banner does not exist) and by a
   *    REQUEST_DENIED status from the Places service.
   *  - the #google-error banner text — populated by index.html's console interception on the
   *    standalone/iframe booker; also lets us extract the exact unauthorized URL Google reports.
   */
  buildGoogleMisconfigError(): any | null {
    const el = (typeof document !== 'undefined') ? document.getElementById('google-error') : null;
    const html = el?.innerHTML?.trim() || '';
    const authError = this._state.googleMapsAuthError;

    if (!html && !authError) {
      return null;
    }

    // Billing problems are a distinct misconfiguration — keep their dedicated message.
    if (html.includes('Billing on the Google Cloud Project')) {
      return { custom: true, description: this._translate.instant('googlemaps_billing_error'), type: 'error', disabled: true };
    }

    // Prefer the exact unauthorized URL Google reports; otherwise fall back to the live host.
    let domain = this._state.googleMapsErrorUrl || window.location.host;
    const match = html.match(/Your site URL to be authorized:\s*(https?:\/\/[^\s<]+)/i);
    if (match && match[1]) {
      domain = match[1];
    }

    const description = this._translate.instant('googlemaps_domain_not_authorized', { domain });
    return { custom: true, description, type: 'error', disabled: true };
  }

  /**
   * Clears a previously-flagged Google Maps misconfiguration once autocomplete works again.
   * Called from the search success paths: if results come back, the domain is authorized, so
   * reset the in-memory flags and the #google-error banner so the resolved state is reflected
   * (otherwise a later empty-result search would still show the stale "not authorized" row).
   */
  clearGoogleMisconfigError(): void {
    if (this._state.googleMapsAuthError) {
      this._state.googleMapsAuthError = false;
      this._state.googleMapsErrorUrl = '';
    }
    const el = (typeof document !== 'undefined') ? document.getElementById('google-error') : null;
    if (el && el.innerHTML) {
      el.innerHTML = '';
      el.style.display = 'none';
    }
  }

  selectFirstOption($event): void {
    if (!this.hasLocationActive && !this._explicitlyCleaned && this.data[0] && !this.data[0].recentAddress) {
      this.getAddress(this.data[0]);
    }
    this._explicitlyCleaned = false;
  }

  setCurrentLocation(): void {
    this.getLocation();
  }

  clear(): void {
    this._explicitlyCleaned = true;
    this.hasErrors = false;
    this.errors.removePropertyErrors(this.config.property);
    const patch = {};
    if (this.config.formArray) {
      patch['stopOvers'] = [];
      patch['stopOversModel'] = [];
      patch['stopOvers'][this.config.property] = '';
      patch['stopOversModel'][this.config.property] = null;
    } else {
      patch[this.config.property] = '';
      patch[`${this.config.property}Model`] = null;
    }
    this.form.patchValue(patch);
    // After clearing, keep the dropdown closed but move focus back to the input so a new address
    // can be typed straight away. Emptying `data` stops the library's open-on-focus; the suppress
    // flag stops the recent-addresses list from popping open for this one programmatic focus.
    this.data = [];
    setTimeout(() => {
      if (this._inputEl) {
        this._suppressRecentOnFocus = true;
        this._inputEl.focus();
        this._suppressRecentOnFocus = false;
      }
    }, 0);
  }

  validityChanged(valid: boolean) {
    this.hasErrors = valid;
  }

  ngOnInit() {
    const _self = this;
    if(!this._state.googleApiKey) {
      this.config.allowBrowserLocation = false;
    }

    if (this.config.googleComponentRestrictions) {
      this.autocompleteSettings.includedRegionCodes = this.config.googleComponentRestrictions.map((c) =>
        c === 'EN' ? 'GB' : c
      );
    }

    this.airports = [];
    Object.keys(AIRPORTS).forEach((key) => {
      if (this.autocompleteSettings.includedRegionCodes && this.autocompleteSettings.includedRegionCodes.includes(key.toUpperCase())) {
        this.airports = this.airports.concat(AIRPORTS[key.toLowerCase()]);
      }
    });
    this.pois = [];

    if (this._state.companySettings && this._state.companySettings.pois && this._state.companySettings.pois.length > 1) {
      this.pois = this._state.companySettings.pois;
    }
  }

  ngAfterViewInit(): void {
    const _self = this;
    const input = document.querySelector(
      `#autoComplete-${this.config.property} input[type=text]`
    ) as HTMLInputElement | null;

    if (input) {
      input.setAttribute('type', 'search');
      input.setAttribute('autocomplete', `${this.config.property}-address`);
      this._inputEl = input;

      if (!this._historyListenerAdded) {
        this._historyListenerAdded = true;
        input.addEventListener('focus', () => {
          // Suppressed for the programmatic focus triggered right after clearing, so clearing
          // focuses the field without popping the dropdown open again.
          if (this._suppressRecentOnFocus) {
            return;
          }
          if (!input.value) {
            const recent = this.getRecentAddresses();
            if (recent.length > 0) {
              this.data = recent;
              setTimeout(() => this.inputAutoComplete?.open(), 100);
            }
          }
        });
      }
    }

    if (typeof (google) === 'undefined' || typeof (google.maps) === 'undefined') {
      setTimeout(function () {
        _self.ngAfterViewInit();
      }, 150);
    } else {
      try {
        // NOTE: AutocompleteService -> AutocompleteSuggestion (no instance needed)
        this.autocompleteSessionToken = new google.maps.places.AutocompleteSessionToken();

        this.results = concat(
          of([]),
          this.input.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => (this.loading = true)),
            switchMap((term: string) => this.searchGoogle(term)),
            tap(() => (this.loading = false))
          )
        );

        if (this.form.value[`${this.config.property}Model`] && this.config.property === 'destination') {
          _self.checkWorkArea.emit(_self.config.property);
        }
        this.address = this.form.value[`${this.config.property}Model`];
      } catch (e) {
        setTimeout(function () {
          _self.ngAfterViewInit();
        }, 150);
      }
    }
  }

  toggleHelpModal(helpTitleTag: string, helpTextTag: string, type?: string, meta?: any) {
    this.openHelpModal.emit({
      helpTitleTag,
      helpTextTag,
      type,
      meta,
    });
  }

  private filterGoogleResults(results: any[]) {
    /**
     * Filter all results that contain an airport code in the description string that we have in the AIRPORTS
     */
    results = results.filter((result) => {
      if (result.description) {
        let found = false;
        this.foundAirports.forEach(airport => {
          found = result.description.indexOf(`\(${airport.code}\)`) > -1;
        });
        return !found;
      }
    });
    return results;
  }
}
